import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';

import { useSystemStore } from 'src/store/SystemStore';

import { withTimeout } from '../promise-timeout';
import { isValidVersionString } from '../version-utils';
import { buildAppVersion, getCurrentAppVersion } from '../app-version';

// Local bridge queries only: resolving a version never checks/downloads an update.
const LOOKUP_TIMEOUT_MS = 4_000;
let pendingResolution: Promise<string> | undefined;
let resolved = false;

function validVersion(value: string | undefined): string | undefined {
  return value && isValidVersionString(value) ? value.trim() : undefined;
}

async function detectVersion(): Promise<string> {
  const [bundleResult, nativeResult] = await Promise.allSettled([
    withTimeout(
      Promise.resolve().then(() => CapacitorUpdater.current()),
      LOOKUP_TIMEOUT_MS,
      'Current bundle version lookup timed out'
    ),
    withTimeout(
      Promise.resolve().then(() => App.getInfo()),
      LOOKUP_TIMEOUT_MS,
      'Native app version lookup timed out'
    ),
  ]);
  const bundle = bundleResult.status === 'fulfilled' ? bundleResult.value?.bundle : undefined;
  const nativeInfo = nativeResult.status === 'fulfilled' ? nativeResult.value : undefined;
  const previous = useSystemStore.getState().appVersion;
  const native = validVersion(nativeInfo?.version) ?? previous.native;
  const ota =
    bundle?.id !== 'builtin' && bundle?.version !== 'builtin'
      ? validVersion(bundle?.version)
      : undefined;
  const current = ota ?? native ?? buildAppVersion;

  useSystemStore.getState().setResolvedAppVersion({
    current,
    native,
    nativeBuild: nativeInfo?.build ?? previous.nativeBuild,
  });
  // Failed bridge calls may be retried by a later consumer. Successful detection
  // is stable for this JS process: activating/resetting an OTA bundle reloads it.
  resolved = bundleResult.status === 'fulfilled' && Boolean(validVersion(nativeInfo?.version));
  return current;
}

/** Resolve and publish the running version, sharing concurrent bridge lookups. */
export function resolveAppVersion(): Promise<string> {
  if (!Capacitor.isNativePlatform()) return Promise.resolve(buildAppVersion);
  if (resolved) return Promise.resolve(getCurrentAppVersion());
  if (!pendingResolution) {
    pendingResolution = detectVersion().finally(() => {
      pendingResolution = undefined;
    });
  }
  return pendingResolution;
}
